import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlass, ArrowRight, Plus, ArrowSquareOut } from "@phosphor-icons/react";
import api from "../lib/api";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { AZIONI_NUOVO } from "./NuovoGlobale";

const COLORI = { "Fattura cliente": "text-emerald-700", "Fornitore/Cliente": "text-slate-900", "Fattura fornitore": "text-slate-700", Bolla: "text-amber-700", Cantiere: "text-sky-700", Operaio: "text-violet-700", Cliente: "text-emerald-700", Incarico: "text-indigo-700", Condominio: "text-rose-700", Movimento: "text-slate-600" };
const ORDINE = ["Cliente", "Incarico", "Cantiere", "Fattura cliente", "Fattura fornitore", "Bolla", "Fornitore/Cliente", "Operaio", "Condominio", "Movimento"];
const norm = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

/** Ricerca globale (lente in alto o Ctrl/Cmd+K): risultati raggruppati per tipo + vai a una pagina + crea nuovo. */
export default function RicercaGlobale({ open, onOpenChange, ambito = "studio", pagine = [] }) {
  const [q, setQ] = useState("");
  const [ris, setRis] = useState([]);
  const [busy, setBusy] = useState(false);
  const [idx, setIdx] = useState(0);
  const timer = useRef(null);
  const navigate = useNavigate();

  useEffect(() => { if (!open) { setQ(""); setRis([]); setIdx(0); } }, [open]);
  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) { setRis([]); return; }
    timer.current = setTimeout(async () => {
      setBusy(true);
      try { const { data } = await api.get("/ricerca", { params: { q: q.trim() } }); setRis(data.risultati); setIdx(0); } catch { setRis([]); }
      setBusy(false);
    }, 250);
    return () => clearTimeout(timer.current);
  }, [q]);

  const nq = norm(q.trim());
  const pagineTrovate = useMemo(() => (nq.length < 2 ? [] : pagine.filter((p, i) => norm(p.label).includes(nq) && pagine.findIndex((x) => x.to === p.to) === i).slice(0, 4)), [pagine, nq]);
  const nqCrea = nq.replace(/^(nuovo|nuova|crea|aggiungi|apri)\s+/, "").trim();
  const creaTrovati = useMemo(() => (nqCrea.length < 2 ? [] : [...(AZIONI_NUOVO[ambito] || []), ...(AZIONI_NUOVO[ambito === "impresa" ? "studio" : "impresa"] || [])].filter((a) => norm(a.label).includes(nqCrea) || norm(a.hint).includes(nqCrea)).slice(0, 3)), [ambito, nqCrea]);
  const gruppi = useMemo(() => {
    const m = new Map();
    ris.forEach((r) => { if (!m.has(r.tipo)) m.set(r.tipo, []); m.get(r.tipo).push(r); });
    return [...m.entries()].sort((a, b) => (ORDINE.indexOf(a[0]) + 100) % 100 - (ORDINE.indexOf(b[0]) + 100) % 100);
  }, [ris]);
  const piatto = useMemo(() => [
    ...pagineTrovate.map((p) => ({ k: "pagina", titolo: p.label, link: p.to })),
    ...creaTrovati.map((a) => ({ k: "crea", titolo: a.label, link: a.to })),
    ...gruppi.flatMap(([, arr]) => arr.map((r) => ({ k: "ris", ...r }))),
  ], [pagineTrovate, creaTrovati, gruppi]);

  const vai = (r) => { onOpenChange(false); navigate(r.link); };
  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setIdx((i) => Math.min(i + 1, piatto.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" && piatto[idx]) vai(piatto[idx]);
  };
  const riga = (item, children) => { const i = piatto.findIndex((x) => x.link === item.link && x.titolo === item.titolo); return (
    <button key={`${item.k || "r"}-${item.link}-${item.titolo}`} type="button" onMouseEnter={() => setIdx(i)} onClick={() => vai(item)} className={`w-full flex items-center gap-3 px-4 py-2 text-left ${i === idx ? "bg-slate-50" : "bg-white"}`} data-testid={`ricerca-risultato-${i}`}>{children}</button>
  ); };
  const titolo = (children) => <div className="px-4 pt-2 pb-1 text-[10px] font-mono uppercase tracking-widest text-slate-400 bg-slate-50/60">{children}</div>;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl p-0 gap-0 overflow-hidden" data-testid="ricerca-globale">
        <DialogTitle className="sr-only">Ricerca</DialogTitle>
        <div className="flex items-center gap-2 border-b border-slate-200 px-3">
          <MagnifyingGlass size={16} className="text-slate-400" />
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey} placeholder="Cerca un cliente, un cantiere, una fattura… oppure una pagina" className="border-0 shadow-none focus-visible:ring-0 h-12 text-base" data-testid="ricerca-input" />
          <kbd className="hidden sm:block text-xs text-slate-400 border border-slate-200 rounded px-1.5 py-0.5 font-mono">Esc</kbd>
        </div>
        <div className="max-h-[60vh] overflow-y-auto" data-testid="ricerca-risultati">
          {nq.length < 2 && (
            <div className="p-4 text-sm text-slate-500 space-y-2">
              <div>Scrivi almeno 2 lettere: trovo clienti, incarichi, cantieri, fatture, bolle, operai e anche le pagine dell'app.</div>
              <div className="text-xs">Esempi: <span className="font-mono">pellegrini</span> · <span className="font-mono">N-2026-013</span> · <span className="font-mono">nuovo preventivo</span> · <span className="font-mono">compensi</span></div>
            </div>
          )}
          {nq.length >= 2 && !busy && piatto.length === 0 && pagineTrovate.length === 0 && creaTrovati.length === 0 && <div className="p-4 text-sm text-slate-500" data-testid="ricerca-vuota">Nessun risultato per «{q}».</div>}
          {pagineTrovate.length > 0 && <>{titolo("Vai a")}{pagineTrovate.map((p) => riga({ k: "pagina", link: p.to, titolo: p.label }, <><ArrowSquareOut size={14} className="text-slate-400" /><span className="flex-1 text-sm">{p.label}</span><ArrowRight size={14} className="text-slate-300" /></>))}</>}
          {creaTrovati.length > 0 && <>{titolo("Crea")}{creaTrovati.map((a) => riga({ k: "crea", link: a.to, titolo: a.label }, <><Plus size={14} weight="bold" className="text-slate-700" /><span className="flex-1 text-sm">Nuovo {a.label.toLowerCase()}</span><ArrowRight size={14} className="text-slate-300" /></>))}</>}
          {gruppi.map(([tipo, arr]) => (
            <div key={tipo} data-testid={`ricerca-gruppo-${tipo.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              {titolo(<><span className={COLORI[tipo] || "text-slate-600"}>{tipo}</span> · {arr.length}</>)}
              {arr.map((r) => riga({ k: "ris", ...r }, <>
                  <span className="flex-1 min-w-0"><span className="block text-sm font-medium text-slate-900 truncate">{r.titolo}</span>{r.dettaglio && <span className="block text-xs text-slate-500 truncate">{r.dettaglio}</span>}</span>
                  <ArrowRight size={14} className="text-slate-300" />
                </>))}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
